// MA-656 round 3 (MF3, RED must-fix) — `number_bond` renderer: the answer-
// safe default picture for the 6 subtraction fact-family items
// (ME0098/0100/0102/0104/0106/0108, "{X} is one part of {W}. What is the
// other part?").
//
// THE GAP THIS CLOSES: round 2 routed these items to the number-bond
// SCAFFOLD only (visualScaffold, HintSlot-reachable via the "NB" hint code)
// — no mathVisual at all, so the concrete band showed nothing by default and
// the bond cost an extra hint tap (H1), exactly what the BACKLOG entry's own
// filed class-1 text asked NOT to ship ("the bond today sits only in the
// hint ladder at the cost of an H1"). This renderer makes the bond a real
// mathVisual — the item's DEFAULT picture, subject to the same fade-by-
// mastery rule (visualScaffoldVisibility.ts) as every other add/sub
// picture, not a second, hint-gated representation of the same thing.
//
// ANSWER-SAFE BY CONSTRUCTION: this component reads ONLY `a` (the known
// part) and `total` (the whole) from props. It NEVER reads `b` — even
// though the mapper's payload carries `b` (the missing part, which for
// these items equals the answer) for contract parity with every other
// renderer, exactly the way ScaffoldNB's `data` carries all three of
// first/second/whole without ever printing the unknown one. The "other
// part" cell always renders the literal glyph "?", never a value derived
// from `b`, `total - a`, or any other computation — so there is no code
// path in this file that can ever draw the answer.
//
// ADDITIVE — registered in rendererRegistry.tsx beside the other built-ins.
// NOT added to mathVisualStage.ts's STAGED_KINDS: the bond IS the authored
// model for these items (same reasoning as `ten_frame`/`ten_frame_sub`),
// not an early counted-all stand-in to fade toward count_on/count_back/
// equation as mastery grows — it renders unchanged at every band.
import type { ReactElement } from "react";
import type { MathRendererProps } from "./rendererRegistry";

function safeInt(n: unknown): number | null {
  const v = Math.floor(Number(n));
  return Number.isFinite(v) && v >= 0 ? v : null;
}

// MA-687 — the CONCRETE bond (`arrangement: "concrete"` or
// `"concrete_known_right"`), drawn for ms.math.05's authored five-frame /
// counters model (FI_CD5_*, FI_EXP2_*): the whole is a row of `total` filled
// five-frame cells, the known part is a circle holding `a` counters, the
// missing part is a dashed circle holding "?". The authored muted extra
// slots (whole 3 or 4) are NOT drawn: an empty cell count equals the answer
// on some records (whole 4 / known 3, whole 3 / known 1), and a child could
// read the empty cells as the missing part.
// Same answer-safety as the numeral bond: only `a` and `total` are read.
// Used only for 1 < whole <= 5 (a five frame); any other payload falls back
// to the numeral bond below, never to a picture of the missing part.
const CONCRETE_ARRANGEMENTS = new Set(["concrete", "concrete_known_right"]);
const FIVE = 5;

/** MA-694 — the NUMERAL bond with the known part in the RIGHT circle and "?"
 *  in the LEFT one, for a blank-left sentence ("___ + 7 = 15"): the ms.math.12
 *  frames project to this bond at secure/fluent (mathVisualStage.ts), and the
 *  "?" sits on the side of the blank (MA-769 C). Not a concrete arrangement:
 *  it draws numerals, exactly like the default numeral bond, only mirrored.
 *  Absent => the default bond (known left, "?" right), byte-identical. */
export const NUMERAL_BOND_KNOWN_RIGHT = "known_right";

/** Counter centres inside a part circle of radius ~30 at (cx, cy), for
 *  1..4 counters (a known part of a whole <= 5 is at most 4). */
function partCounterPositions(n: number, cx: number, cy: number): [number, number][] {
  const d = 11;
  switch (n) {
    case 1: return [[cx, cy]];
    case 2: return [[cx - d, cy], [cx + d, cy]];
    case 3: return [[cx - d, cy + d * 0.8], [cx + d, cy + d * 0.8], [cx, cy - d * 0.9]];
    default: return [[cx - d, cy - d], [cx + d, cy - d], [cx - d, cy + d], [cx + d, cy + d]];
  }
}

function ConcreteNumberBond({ known, whole, knownRight, ariaLabel }: { known: number; whole: number; knownRight: boolean; ariaLabel: string }): ReactElement {
  const cell = 28;
  const frameX = 120 - (whole * cell) / 2;
  const frameY = 8;
  const knownX = knownRight ? 175 : 65;
  const missingX = knownRight ? 65 : 175;
  const partY = 122;
  return (
    <svg
      viewBox="0 0 240 160"
      className="w-full"
      style={{ maxWidth: 300 }}
      role="img"
      aria-label={ariaLabel}
      data-bond-arrangement={knownRight ? "concrete_known_right" : "concrete"}
    >
      <line x1="120" y1={frameY + cell} x2={knownX} y2={partY - 30} stroke="var(--ink, #3d3228)" strokeWidth="2" />
      <line x1="120" y1={frameY + cell} x2={missingX} y2={partY - 30} stroke="var(--ink, #3d3228)" strokeWidth="2" />
      {/* The whole: `whole` filled frame cells (no empty cells — see above). */}
      <g data-bond-part="whole">
        {Array.from({ length: whole }, (_, i) => (
          <g key={i}>
            <rect
              x={frameX + i * cell}
              y={frameY}
              width={cell}
              height={cell}
              fill="var(--cream, #FAF3E8)"
              stroke="var(--ink, #3d3228)"
              strokeWidth="1.5"
            />
            <circle data-counter="whole" cx={frameX + i * cell + cell / 2} cy={frameY + cell / 2} r="9" fill="var(--gold, #e8b84b)" stroke="var(--ink, #3d3228)" strokeWidth="1" />
          </g>
        ))}
      </g>
      {/* The known part: a circle holding `known` counters. */}
      <g data-bond-part="known">
        <circle cx={knownX} cy={partY} r="30" fill="var(--cream, #FAF3E8)" stroke="var(--ink, #3d3228)" strokeWidth="1.5" />
        {partCounterPositions(known, knownX, partY).map(([x, y], i) => (
          <circle key={i} data-counter="known" cx={x} cy={y} r="8" fill="var(--terracotta, #c96b4a)" stroke="var(--ink, #3d3228)" strokeWidth="1" />
        ))}
      </g>
      {/* The missing part: NEVER drawn — a dashed circle and "?". */}
      <g data-bond-part="missing">
        <circle cx={missingX} cy={partY} r="30" fill="none" stroke="var(--ink, #3d3228)" strokeWidth="1.5" strokeDasharray="5 4" />
        <text x={missingX} y={partY + 8} textAnchor="middle" fontSize="24" fontFamily="Nunito, inherit" fontWeight={800}>
          ?
        </text>
      </g>
    </svg>
  );
}

export default function NumberBondRenderer({ a, total, arrangement }: MathRendererProps): ReactElement {
  const known = safeInt(a);
  const whole = safeInt(total);
  // Fail-safe: never throw, never draw a bond with a garbled/degenerate
  // payload (mirrors the renderabilityAudit contract: 0 < known < whole).
  if (known === null || whole === null || known <= 0 || known >= whole) {
    return <svg viewBox="0 0 10 10" width="0" height="0" role="img" aria-label="Number bond" />;
  }
  const ariaLabel = `Number bond. The whole is ${whole}. One part is ${known}. The other part is missing.`;
  const knownRight = arrangement === NUMERAL_BOND_KNOWN_RIGHT;
  if (typeof arrangement === "string" && CONCRETE_ARRANGEMENTS.has(arrangement) && whole <= FIVE) {
    return <ConcreteNumberBond known={known} whole={whole} knownRight={arrangement === "concrete_known_right"} ariaLabel={ariaLabel} />;
  }
  return (
    <svg
      viewBox="0 0 200 110"
      className="w-full"
      style={{ maxWidth: 260 }}
      role="img"
      aria-label={ariaLabel}
      data-bond-arrangement={knownRight ? NUMERAL_BOND_KNOWN_RIGHT : undefined}
    >
      <line x1="100" y1="34" x2="55" y2="80" stroke="var(--ink, #3d3228)" strokeWidth="2" />
      <line x1="100" y1="34" x2="145" y2="80" stroke="var(--ink, #3d3228)" strokeWidth="2" />
      <circle cx="100" cy="22" r="20" fill="var(--gold, #e8b84b)" stroke="var(--ink, #3d3228)" strokeWidth="1.5" />
      <text x="100" y="27" textAnchor="middle" fontSize="16" fontFamily="Nunito, inherit" fontWeight={800}>
        {whole}
      </text>
      {knownRight ? (
        <>
          {/* MA-694 — mirrored: "?" LEFT (the blank's side), known RIGHT.
              Drawn left-to-right so the DOM order stays (whole, left, right). */}
          <circle cx="55" cy="86" r="18" fill="var(--pal-blue, #5b8fc9)" stroke="var(--ink, #3d3228)" strokeWidth="1.5" />
          <text x="55" y="91" textAnchor="middle" fontSize="14" fontFamily="Nunito, inherit" fontWeight={800}>
            ?
          </text>
          <circle cx="145" cy="86" r="18" fill="var(--terracotta, #c96b4a)" stroke="var(--ink, #3d3228)" strokeWidth="1.5" />
          <text x="145" y="91" textAnchor="middle" fontSize="14" fontFamily="Nunito, inherit" fontWeight={800} fill="white">
            {known}
          </text>
        </>
      ) : (
        <>
          <circle cx="55" cy="86" r="18" fill="var(--terracotta, #c96b4a)" stroke="var(--ink, #3d3228)" strokeWidth="1.5" />
          <text x="55" y="91" textAnchor="middle" fontSize="14" fontFamily="Nunito, inherit" fontWeight={800} fill="white">
            {known}
          </text>
          {/* The other part is NEVER printed — always the "?" glyph, regardless
              of the `b` prop's value. */}
          <circle cx="145" cy="86" r="18" fill="var(--pal-blue, #5b8fc9)" stroke="var(--ink, #3d3228)" strokeWidth="1.5" />
          <text x="145" y="91" textAnchor="middle" fontSize="14" fontFamily="Nunito, inherit" fontWeight={800}>
            ?
          </text>
        </>
      )}
    </svg>
  );
}
